import { NodeSelection } from '@tiptap/pm/state';
import { Node as ProseMirrorNode } from 'prosemirror-model';

export interface ResizableMediaOptions {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  HTMLAttributes: Record<string, unknown>;
  minWidth: number;
  maxWidth: number;
}

export interface ResizableMediaNodeAttrs {
  src: string;
  alt?: string;
  title?: string;
  width: number;
  align: string;
  [key: string]: unknown;
}

export interface MediaNodeViewConfig {
  nodeName: string;
  tagName: 'img' | 'video' | 'audio';
  extraAttributes?: Record<string, unknown>;
}

// Types pour les attributs média
interface MediaAttributes {
  controls?: boolean;
  preload?: string;
}

type MediaElement = HTMLImageElement | HTMLVideoElement | HTMLAudioElement;

export function getAlignmentStyle(align: string): string {
  switch (align) {
    case 'center':
      return 'margin: 10px auto;';
    case 'right':
      return 'margin: 10px 0 10px auto;';
    case 'left':
    default:
      return 'margin: 10px auto 10px 0;';
  }
}

/**
 * Applique les attributs spécifiques aux éléments vidéo et audio
 * Optimisé pour éviter les modifications inutiles
 */
function applyMediaAttributes(element: MediaElement, attrs: MediaAttributes): void {
  if (element.tagName === 'IMG') {
    return;
  }

  const mediaElement = element as HTMLVideoElement | HTMLAudioElement;

  // Toujours ajouter controls pour les vidéos et audios (si pas déjà présent)
  if (!mediaElement.hasAttribute('controls')) {
    mediaElement.setAttribute('controls', 'controls');
  }

  // Gestion de l'attribut preload (seulement si différent)
  if (attrs.preload !== undefined) {
    const currentPreload = mediaElement.getAttribute('preload');
    if (currentPreload !== attrs.preload) {
      mediaElement.setAttribute('preload', attrs.preload);
    }
  }
}

/**
 * Met à jour les attributs d'un élément média
 * Optimisé pour éviter les modifications inutiles
 */
function updateMediaAttributes(element: MediaElement, attrs: ResizableMediaNodeAttrs, tagName: string): void {
  // Attributs de base - seulement si différents
  if (element.src !== attrs.src) {
    element.src = attrs.src;
  }

  const newTitle = attrs.title || '';
  if (element.title !== newTitle) {
    element.title = newTitle;
  }

  if (tagName === 'img') {
    const imgElement = element as HTMLImageElement;
    const newAlt = attrs.alt || '';
    if (imgElement.alt !== newAlt) {
      imgElement.alt = newAlt;
    }
  } else {
    // Pour vidéo et audio
    applyMediaAttributes(element, attrs as MediaAttributes);

    // Pause le média lors de la mise à jour (seulement si pas déjà en pause)
    const mediaElement = element as HTMLVideoElement | HTMLAudioElement;
    if (!mediaElement.paused) {
      mediaElement.pause();
    }
  }
}

/**
 * Configure les attributs initiaux d'un élément média
 */
function setupMediaElement(element: MediaElement, attrs: ResizableMediaNodeAttrs, config: MediaNodeViewConfig): void {
  // Attributs de base
  element.src = attrs.src;
  element.title = attrs.title || '';
  element.setAttribute('data-drag-handle', 'true');

  if (config.tagName === 'img') {
    (element as HTMLImageElement).alt = attrs.alt || '';
  } else {
    // Pour vidéo et audio
    applyMediaAttributes(element, attrs as MediaAttributes);
  }

  // Appliquer les attributs extra (legacy support) - seulement si différents
  if (config.extraAttributes) {
    Object.entries(config.extraAttributes).forEach(([key, value]) => {
      if (attrs[key] !== undefined) {
        if (typeof value === 'boolean') {
          if (attrs[key]) {
            const currentValue = element.getAttribute(key);
            const newValue = value as unknown as string;
            if (currentValue !== newValue) {
              element.setAttribute(key, newValue);
            }
          }
        } else {
          const currentValue = element.getAttribute(key);
          const newValue = attrs[key] as string;
          if (currentValue !== newValue) {
            element.setAttribute(key, newValue);
          }
        }
      }
    });
  }
}

export function createResizableMediaView(config: MediaNodeViewConfig, options: ResizableMediaOptions): any {
  return ({ node, getPos, editor }: any): any => {
    const container = document.createElement('div');
    container.setAttribute('data-type', `resizable-${config.nodeName}`);
    container.setAttribute('data-width', node.attrs.width.toString());
    container.setAttribute('data-align', node.attrs.align || 'left');

    const align = node.attrs.align || 'left';
    const alignStyle = getAlignmentStyle(align);

    container.style.cssText = `
      position: relative;
      width: 100%;
      display: block;
      border: 2px solid transparent;
      border-radius: 4px;
      transition: border-color 0.2s ease;
      cursor: pointer;
    `;

    const media = document.createElement(config.tagName);
    setupMediaElement(media, node.attrs, config);

    media.style.cssText = `
      width: ${node.attrs.width}%;
      display: block;
      border-radius: 2px;
      cursor: grab;
      ${alignStyle}
    `;

    // Resize handle
    const resizeHandle = document.createElement('div');
    resizeHandle.setAttribute('data-resize-handle', 'true');
    resizeHandle.innerHTML = `<mat-icon class="material-symbols-rounded">open_in_full</mat-icon>`;
    resizeHandle.style.cssText = `
      position: absolute;
      bottom: -8px;
      right: -8px;
      padding: 4px;
      background: var(--mat-sys-primary);
      color: white;
      border: 2px solid white;
      border-radius: 50%;
      cursor: se-resize;
      display: none;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      z-index: 10;
      user-select: none;
      transition: all 0.2s ease;
    `;

    // Percentage indicator
    const percentageIndicator = document.createElement('div');
    percentageIndicator.style.cssText = `
      position: absolute;
      bottom: 30px;
      right: 0;
      background: #1f2937;
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      display: none;
      z-index: 10;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      white-space: nowrap;
    `;

    // Hover indicator
    const hoverIndicator = document.createElement('div');
    hoverIndicator.innerHTML = '⤢';
    hoverIndicator.style.cssText = `
      position: absolute;
      top: 8px;
      right: 8px;
      width: 20px;
      height: 20px;
      background: rgba(0, 0, 0, 0.6);
      color: white;
      border-radius: 50%;
      display: none;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
    `;

    // Resize functionality
    let isResizing = false;
    let startX = 0;
    let startWidth = 0;
    let containerRect: DOMRect;

    const updatePercentageDisplay = (): void => {
      const newText = `${node.attrs.width}%`;
      if (percentageIndicator.textContent !== newText) {
        percentageIndicator.textContent = newText;
      }
    };

    const startResize = (e: MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      isResizing = true;
      startX = e.clientX;
      startWidth = node.attrs.width;

      const editorElement = container.closest('.tiptap-editor-content') || container.parentElement;
      containerRect = editorElement.getBoundingClientRect();

      updatePercentageDisplay();

      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', stopResize);
      document.body.style.cursor = 'se-resize';
    };

    const handleResize = (e: MouseEvent): void => {
      if (!isResizing) {
        return;
      }

      const deltaX = e.clientX - startX;
      const containerWidth = containerRect.width;
      const deltaPercentage = (deltaX / containerWidth) * 100;

      let newWidth = Math.round(startWidth + deltaPercentage);
      newWidth = Math.max(options.minWidth, Math.min(options.maxWidth, newWidth));

      // Éviter les modifications inutiles pendant le redimensionnement
      const currentWidth = container.getAttribute('data-width');
      if (currentWidth !== newWidth.toString()) {
        media.style.width = `${newWidth}%`;
        container.setAttribute('data-width', newWidth.toString());

        const newPercentageText = `${newWidth}%`;
        if (percentageIndicator.textContent !== newPercentageText) {
          percentageIndicator.textContent = newPercentageText;
        }
      }
    };

    const stopResize = (): void => {
      if (!isResizing) {
        return;
      }

      isResizing = false;
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', stopResize);
      document.body.style.cursor = '';

      const newWidth = parseInt(container.getAttribute('data-width'), 10);
      if (newWidth !== node.attrs.width) {
        editor.chain().focus().updateAttributes(`resizable-${config.nodeName}`, { width: newWidth }).run();
      }
    };

    const handleSelection = (selected: boolean): void => {
      if (selected) {
        container.style.borderColor = 'var(--mat-sys-primary)';
        resizeHandle.style.display = 'flex';
        hoverIndicator.style.display = 'none';
        percentageIndicator.style.display = 'block';
        updatePercentageDisplay();
      } else {
        container.style.borderColor = 'transparent';
        resizeHandle.style.display = 'none';
        percentageIndicator.style.display = 'none';
      }
    };

    // Event listeners
    container.addEventListener('mouseenter', () => {
      if (resizeHandle.style.display === 'none') {
        hoverIndicator.style.display = 'flex';
      }
    });

    container.addEventListener('mouseleave', () => {
      if (resizeHandle.style.display === 'none') {
        hoverIndicator.style.display = 'none';
      }
    });

    container.addEventListener('click', e => {
      e.preventDefault();
      if (typeof getPos === 'function') {
        const { view } = editor;
        const pos = getPos();
        const nodeSelection = NodeSelection.create(view.state.doc, pos);
        view.dispatch(view.state.tr.setSelection(nodeSelection));
        view.focus();
      }
    });

    resizeHandle.addEventListener('mousedown', startResize);

    media.addEventListener('dragstart', e => {
      if (isResizing) {
        e.preventDefault();
        return;
      }
      media.style.cursor = 'grabbing';
    });

    media.addEventListener('dragend', () => {
      media.style.cursor = 'grab';
    });

    // Assembly
    container.appendChild(media);
    container.appendChild(resizeHandle);
    container.appendChild(percentageIndicator);
    container.appendChild(hoverIndicator);

    updatePercentageDisplay();

    return {
      dom: container,
      update: (updatedNode: ProseMirrorNode): boolean => {
        if (updatedNode.type !== node.type) {
          return false;
        }

        // Sauvegarder les anciennes valeurs avant l'assignation
        const oldWidth = Number(node.attrs.width) || 100;
        const oldAlign = node.attrs.align || 'left';

        Object.assign(node, updatedNode);

        // Mise à jour des attributs média
        updateMediaAttributes(media, updatedNode.attrs as ResizableMediaNodeAttrs, config.tagName);

        // Mise à jour des attributs extra (legacy support) - seulement si différents
        if (config.extraAttributes) {
          Object.entries(config.extraAttributes).forEach(([key, value]) => {
            if (updatedNode.attrs[key] !== undefined) {
              if (typeof value === 'boolean') {
                const currentValue = media.getAttribute(key);
                const newValue = value as unknown as string;
                if (currentValue !== newValue) {
                  media.setAttribute(key, newValue);
                }
              } else {
                const currentValue = media.getAttribute(key);
                const newValue = updatedNode.attrs[key] as string;
                if (currentValue !== newValue) {
                  media.setAttribute(key, newValue);
                }
              }
            }
          });
        }

        const newWidth = Number(updatedNode.attrs.width) || 100;
        const newAlign = updatedNode.attrs.align || 'left';

        // Mise à jour des attributs du container seulement si différents
        const currentWidth = container.getAttribute('data-width');
        if (currentWidth !== newWidth.toString()) {
          container.setAttribute('data-width', newWidth.toString());
        }

        const currentAlign = container.getAttribute('data-align');
        if (currentAlign !== newAlign) {
          container.setAttribute('data-align', newAlign);
        }

        // Mise à jour du style seulement si la largeur ou l'alignement a changé
        if (oldWidth !== newWidth || oldAlign !== newAlign) {
          const alignStyleMedia = getAlignmentStyle(newAlign);
          media.style.cssText = `
            width: ${newWidth}%;
            display: block;
            border-radius: 2px;
            cursor: grab;
            ${alignStyleMedia}
          `;
        }

        updatePercentageDisplay();
        return true;
      },
      selectNode: () => handleSelection(true),
      deselectNode: () => handleSelection(false),
      destroy: (): void => {
        document.removeEventListener('mousemove', handleResize);
        document.removeEventListener('mouseup', stopResize);
        document.body.style.cursor = '';
      },
    };
  };
}

export function createResizableMediaCommands(nodeName: string): Record<string, (...args: unknown[]) => unknown> {
  return {
    [`setResizable${nodeName.charAt(0).toUpperCase() + nodeName.slice(1)}`]:
      (options: ResizableMediaNodeAttrs) =>
      ({ commands }: any): void => {
        commands.insertContent({
          type: `resizable-${nodeName}`,
          attrs: {
            ...options,
            width: options.width || 100,
            align: options.align || 'left',
          },
        });
      },
    [`updateResizable${nodeName.charAt(0).toUpperCase() + nodeName.slice(1)}Width`]:
      (width: number) =>
      ({ commands, state }: any): boolean => {
        const { from } = state.selection;
        const node = state.doc.nodeAt(from);
        if (node && node.type.name === `resizable-${nodeName}`) {
          return commands.updateAttributes(`resizable-${nodeName}`, { width });
        }
        return false;
      },
    [`updateResizable${nodeName.charAt(0).toUpperCase() + nodeName.slice(1)}Align`]:
      (align: string) =>
      ({ commands, state }: any): boolean => {
        const { from } = state.selection;
        const node = state.doc.nodeAt(from);
        if (node && node.type.name === `resizable-${nodeName}`) {
          return commands.updateAttributes(`resizable-${nodeName}`, { align });
        }
        return false;
      },
  };
}
